import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';

import { getCelebratedPersonName } from '../../../../../apps/pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/helpers';
import { STRIPE_CHECKOUT_MODAL } from '../../../../../components/global/StripeCheckout';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../constants/account-referrals';
import useGiftingEventAddonPurchase from '../../../../../apps/pro/components/GiftingEventAddonItemsPurchase/hooks/useGiftingEventAddonPurchase';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import { getErrorString } from '../../../../../util/normalize-errors';
import useOpenModal from '../../../../../hooks/useOpenModal';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import PurchaseStep from '../../../../../apps/pro/components/GiftingEventAddonItemsPurchase/components/PurchaseStep';
import Spinner from '../../../../../components/global/Spinner';
import { STEPS } from '../../constants';
import Angle from '../../../../../components/global/Angle';
import Button from '../../../../../components/global/Button';
import { PHONE_AUTH_MODAL } from '../../../../../constants/modals';
import { trackAuthEvent } from '../../../../../apps/web-gallery/utilities/analytics';
import PhoneAuthModal from '../../../../../components/auth/PhoneAuthModal';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  onBack: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
};

const GiftingStep = ({ albumFolder, user, onBack, setStep }) => {
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const celebratedName = getCelebratedPersonName(
    albumFolder?.album_subtype,
    albumFolder?.event_settings,
    false,
    albumFolder?.account?.organization_name ?? albumFolder?.account?.name,
  );

  const {
    loadingEventAddonItems,
    eventAddonItems,
    onCheckout,
    submitting,
    selectedEventAddonItemIds,
    onEventAddonItemToggle,
    success,
  } = useGiftingEventAddonPurchase({
    albumFolderId: albumFolder?.id,
    defaultEventAddonItemTypes: [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    ],
    excludePurchased: true,
    onCheckoutError: ({ error }) =>
      openAlert({
        message: 'There was an error purchasing the selected items',
        info: getErrorString(error),
      }),
  });

  useEffect(() => {
    if (success) {
      setStep(STEPS.GIFTING_SUCCESS);
    }
  }, [success]);

  const openPurchaseModal = () => {
    openModal(STRIPE_CHECKOUT_MODAL, {
      buttonLabel: 'Complete payment',
      // If the event is FREE, will not require CC
      cardRequired: false,
      onSuccess: token =>
        onCheckout({
          token,
          albumFolderId: albumFolder.id,
          eventAddonItemIds: selectedEventAddonItemIds,
        }),
      accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
      checkoutPriceVariables: ({ promo_code: promoCode }) => {
        return {
          event_addon_items: selectedEventAddonItemIds.map(id => ({
            event_addon_item_id: id,
            quantity: 1,
            album_folder_id: albumFolder.id,
            for_gift: true,
          })),
          promo_code: promoCode,
        };
      },
      defaultName: user?.name ?? '',
      defaultEmail: user?.email_address ?? '',
    });
  };

  const onPurchase = () => {
    if (!albumFolder) {
      // We cannot perform a checkout without albumFolderId
      return;
    }

    if (!user) {
      openModal(PHONE_AUTH_MODAL, {
        introText: (
          <Fragment>
            Enter your mobile phone below to proceed with your gift purchase!
          </Fragment>
        ),
        submitText: 'Send PIN Code',
        onLogin: openPurchaseModal,
      });

      return;
    }

    openPurchaseModal();
  };

  return (
    <Fragment>
      <Button
        btnStyle="link"
        className="specialty-event-upload-btn-dismiss gifting-step-dismiss"
        onClick={onBack}
      >
        <Angle direction="left" />
      </Button>
      <div className="d-flex flex-column align-items-center justify-content-center">
        <h2 className="text-black mb-2 text-center">
          Give {celebratedName} a gift of memories!
        </h2>
      </div>
      {loadingEventAddonItems ? (
        <div className="text-center">
          <Spinner />
        </div>
      ) : (
        <PurchaseStep
          withHeader={false}
          selectedEventAddonItemIds={selectedEventAddonItemIds}
          onEventAddonItemToggle={onEventAddonItemToggle}
          eventAddonItems={eventAddonItems}
          onPurchase={onPurchase}
          submitting={submitting}
        />
      )}
      <PhoneAuthModal onLogin={trackAuthEvent} />
    </Fragment>
  );
};

GiftingStep.propTypes = propTypes;

export default GiftingStep;
